import { dashboardService } from './dashboardService';
import { getOrganizationName } from './data';
import { supabase } from './supabaseClient';

const CHAT_MODEL = 'gpt-4o-mini';
const DATA_PACK_LIMITS = {
  members: 2000,
  donations: 5000,
  events: 2000,
  attendance: 5000,
  tasks: 2000,
  groups: 1000,
  groupMembers: 5000,
  families: 5000,
  smsMessages: 2000,
  emailMessages: 2000
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value) => {
  return toNumber(value).toLocaleString();
};

const formatCurrency = (value) => {
  return `$${toNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
};

const truncateText = (value, maxLength = 200) => {
  if (!value || typeof value !== 'string') return value;
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1)}…`;
};

const sanitizeRow = (row, textFields = []) => {
  if (!row) return row;
  const sanitized = { ...row };
  textFields.forEach((field) => {
    if (sanitized[field]) {
      sanitized[field] = truncateText(String(sanitized[field]));
    }
  });
  return sanitized;
};

const getDonationDate = (donation) => {
  const dateValue = donation?.date || donation?.created_at;
  if (!dateValue) return null;
  const parsed = dateValue.includes('T') ? new Date(dateValue) : new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildTopDonors = (donations, members) => {
  if (!donations || donations.length === 0) return [];

  const memberMap = new Map(
    (members || []).map((member) => [member.id, `${member.firstname || ''} ${member.lastname || ''}`.trim()])
  );
  const now = new Date();
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setMonth(now.getMonth() - 12);

  const donorStats = donations.reduce((acc, donation) => {
    const donationDate = getDonationDate(donation);
    if (donationDate && donationDate < twelveMonthsAgo) {
      return acc;
    }

    const donorId = donation.donor_id;
    const donorName =
      donation.donor_name ||
      (donorId ? memberMap.get(donorId) : null) ||
      'Anonymous';
    const key = donorId || donorName;

    if (!acc[key]) {
      acc[key] = {
        name: donorName,
        totalAmount: 0,
        donationCount: 0
      };
    }
    acc[key].totalAmount += Number(donation.amount) || 0;
    acc[key].donationCount += 1;
    return acc;
  }, {});

  return Object.values(donorStats)
    .sort((a, b) => b.totalAmount - a.totalAmount)
    .slice(0, 5);
};

const createDataSet = async (label, query, limit, textFields = []) => {
  try {
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data || []).map((row) => sanitizeRow(row, textFields));
    return {
      rows,
      truncated: rows.length >= limit,
      error: null
    };
  } catch (error) {
    console.warn(`AI chat data pack failed for ${label}:`, error);
    return {
      rows: [],
      truncated: false,
      error: error.message || 'Unavailable'
    };
  }
};

const buildDataPack = async (organizationId) => {
  if (!organizationId) {
    return { meta: { error: 'Missing organization ID' } };
  }

  const [
    membersData,
    donationsData,
    eventsData,
    tasksData,
    groupsData,
    groupMembersData,
    familiesData,
    smsMessagesData,
    emailMessagesData
  ] = await Promise.all([
    createDataSet(
      'members',
      supabase
        .from('members')
        .select('id, firstname, lastname, status, member_type, join_date, birth_date, email, phone')
        .eq('organization_id', organizationId)
        .order('lastname', { ascending: true })
        .limit(DATA_PACK_LIMITS.members),
      DATA_PACK_LIMITS.members
    ),
    createDataSet(
      'donations',
      supabase
        .from('donations')
        .select('id, donor_id, donor_name, amount, date, fund_designation, payment_method, is_anonymous, notes')
        .eq('organization_id', organizationId)
        .order('date', { ascending: false })
        .limit(DATA_PACK_LIMITS.donations),
      DATA_PACK_LIMITS.donations,
      ['notes']
    ),
    createDataSet(
      'events',
      supabase
        .from('events')
        .select('id, title, start_date, end_date, event_type, needs_volunteers, location, status')
        .eq('organization_id', organizationId)
        .order('start_date', { ascending: false })
        .limit(DATA_PACK_LIMITS.events),
      DATA_PACK_LIMITS.events,
      ['title', 'location']
    ),
    createDataSet(
      'tasks',
      supabase
        .from('tasks')
        .select('id, title, status, priority, due_date, assignee_id, requestor_id, category')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(DATA_PACK_LIMITS.tasks),
      DATA_PACK_LIMITS.tasks,
      ['title', 'category']
    ),
    createDataSet(
      'groups',
      supabase
        .from('groups')
        .select('id, name, description, leader_id, created_at')
        .eq('organization_id', organizationId)
        .order('name', { ascending: true })
        .limit(DATA_PACK_LIMITS.groups),
      DATA_PACK_LIMITS.groups,
      ['name', 'description']
    ),
    createDataSet(
      'group_members',
      supabase
        .from('group_members')
        .select('group_id, member_id, role')
        .eq('organization_id', organizationId)
        .limit(DATA_PACK_LIMITS.groupMembers),
      DATA_PACK_LIMITS.groupMembers,
      ['role']
    ),
    createDataSet(
      'families',
      supabase
        .from('family_members_view')
        .select('family_id, family_name, member_id, firstname, lastname, relationship_type, is_primary')
        .eq('family_organization_id', organizationId)
        .order('family_name', { ascending: true })
        .limit(DATA_PACK_LIMITS.families),
      DATA_PACK_LIMITS.families,
      ['family_name', 'firstname', 'lastname', 'relationship_type']
    ),
    createDataSet(
      'sms_messages',
      supabase
        .from('sms_messages')
        .select('id, direction, body, sent_at, status, member_id, from_number, to_number')
        .eq('organization_id', organizationId)
        .order('sent_at', { ascending: false })
        .limit(DATA_PACK_LIMITS.smsMessages),
      DATA_PACK_LIMITS.smsMessages,
      ['body']
    ),
    createDataSet(
      'email_messages',
      supabase
        .from('email_messages')
        .select('id, subject, status, sent_at, to_email, from_email')
        .eq('organization_id', organizationId)
        .order('sent_at', { ascending: false })
        .limit(DATA_PACK_LIMITS.emailMessages),
      DATA_PACK_LIMITS.emailMessages,
      ['subject']
    )
  ]);

  let attendanceData = { rows: [], truncated: false, error: null };
  const eventIds = eventsData.rows.map((event) => event.id).filter(Boolean);
  if (eventIds.length > 0) {
    attendanceData = await createDataSet(
      'event_attendance',
      supabase
        .from('event_attendance')
        .select('event_id, member_id, status, created_at')
        .in('event_id', eventIds.slice(0, 200))
        .order('created_at', { ascending: false })
        .limit(DATA_PACK_LIMITS.attendance),
      DATA_PACK_LIMITS.attendance
    );
  }

  return {
    meta: {
      organizationId,
      generatedAt: new Date().toISOString(),
      limits: DATA_PACK_LIMITS,
      notes: [
        'Attendance records are limited to the most recent 200 events in this pack.',
        'Text fields are truncated to keep the payload compact.'
      ]
    },
    members: membersData,
    donations: donationsData,
    events: eventsData,
    attendance: attendanceData,
    tasks: tasksData,
    groups: groupsData,
    groupMembers: groupMembersData,
    families: familiesData,
    smsMessages: smsMessagesData,
    emailMessages: emailMessagesData
  };
};

const buildSnapshot = (organizationName, dashboardData, dataPack) => {
  if (!dashboardData) return null;

  const members = dashboardData.members?.counts || {};
  const donations = dashboardData.donations?.stats || {};
  const donationsAll = dashboardData.donations?.all || [];
  const membersAll = dashboardData.members?.all || [];
  const events = dashboardData.events?.stats || {};
  const tasks = dashboardData.tasks?.stats || {};
  const attendance = dashboardData.attendance || {};
  const sms = dashboardData.sms || {};
  const celebrations = dashboardData.celebrations || {};
  const topDonors = buildTopDonors(donationsAll, membersAll);
  const uniqueDonors = new Set(
    donationsAll.map((donation) => donation.donor_id || donation.donor_name || 'Anonymous')
  ).size;

  return {
    generatedAt: new Date().toISOString(),
    organizationName: organizationName || 'your church',
    members: {
      total: toNumber(members.total),
      active: toNumber(members.active),
      inactive: toNumber(members.inactive),
      visitors: toNumber(members.visitors)
    },
    donations: {
      total: toNumber(donations.total),
      monthly: toNumber(donations.monthly),
      lastMonth: toNumber(donations.lastMonth),
      growthRate: toNumber(donations.growthRate),
      weeklyAverage: toNumber(donations.weeklyAverage),
      monthlyAverage: toNumber(donations.monthlyAverage)
    },
    events: {
      total: toNumber(events.total),
      upcoming: toNumber(events.upcoming),
      thisWeek: toNumber(events.thisWeek),
      thisMonth: toNumber(events.thisMonth),
      mostCommonType: events.mostCommonType || 'None',
      needingVolunteers: toNumber(events.needingVolunteers)
    },
    attendance: {
      sundayServiceRate: toNumber(attendance.sundayServiceRate),
      sundayServiceAverage: toNumber(attendance.sundayServicePercentage),
      bibleStudyAverage: toNumber(attendance.bibleStudyPercentage),
      fellowshipAverage: toNumber(attendance.fellowshipPercentage)
    },
    tasks: {
      total: toNumber(tasks.total),
      pending: toNumber(tasks.pending),
      overdue: toNumber(tasks.overdue)
    },
    donors: {
      topDonors,
      uniqueDonors,
      timeframe: 'last 12 months'
    },
    sms: {
      activeConversations: toNumber(sms.activeConversations),
      recentMessages: toNumber(sms.recentMessages)
    },
    celebrations: {
      upcomingBirthdays: toNumber(celebrations.upcomingBirthdays),
      upcomingAnniversaries: toNumber(celebrations.upcomingAnniversaries),
      upcomingMemberships: toNumber(celebrations.upcomingMemberships),
      totalUpcoming: toNumber(celebrations.totalUpcoming)
    },
    dataPack
  };
};

const buildContext = (snapshot) => {
  if (!snapshot) {
    return 'No church data snapshot is available.';
  }

  const dataPackContext = snapshot.dataPack
    ? `Full data pack (JSON, limits apply): ${JSON.stringify(snapshot.dataPack)}`
    : 'Full data pack not available.';

  return [
    `Organization: ${snapshot.organizationName}`,
    `Snapshot generated at: ${snapshot.generatedAt}`,
    `Members: ${formatNumber(snapshot.members.total)} total (${formatNumber(snapshot.members.active)} active, ${formatNumber(snapshot.members.inactive)} inactive, ${formatNumber(snapshot.members.visitors)} visitors).`,
    `Attendance: Sunday service participation rate ${formatNumber(snapshot.attendance.sundayServiceRate)}% (avg attendance ${formatNumber(snapshot.attendance.sundayServiceAverage)}). Bible study avg ${formatNumber(snapshot.attendance.bibleStudyAverage)}. Fellowship avg ${formatNumber(snapshot.attendance.fellowshipAverage)}.`,
    `Giving: total ${formatCurrency(snapshot.donations.total)}. This month ${formatCurrency(snapshot.donations.monthly)} vs last month ${formatCurrency(snapshot.donations.lastMonth)} (growth ${formatNumber(snapshot.donations.growthRate)}%).`,
    snapshot.donors.topDonors.length > 0
      ? `Top givers (${snapshot.donors.timeframe}): ${snapshot.donors.topDonors
          .map((donor) => `${donor.name} (${formatCurrency(donor.totalAmount)})`)
          .join(', ')}.`
      : `Top givers (${snapshot.donors.timeframe}): not enough data.`,
    `Events: ${formatNumber(snapshot.events.total)} total, ${formatNumber(snapshot.events.upcoming)} upcoming, ${formatNumber(snapshot.events.thisWeek)} this week, ${formatNumber(snapshot.events.thisMonth)} this month. Most common type: ${snapshot.events.mostCommonType}. Events needing volunteers: ${formatNumber(snapshot.events.needingVolunteers)}.`,
    `Tasks: ${formatNumber(snapshot.tasks.total)} total (${formatNumber(snapshot.tasks.pending)} pending, ${formatNumber(snapshot.tasks.overdue)} overdue).`,
    `Messaging: ${formatNumber(snapshot.sms.activeConversations)} active SMS conversations, ${formatNumber(snapshot.sms.recentMessages)} messages in last 30 days.`,
    `Upcoming celebrations (next 30 days): ${formatNumber(snapshot.celebrations.totalUpcoming)} total (birthdays ${formatNumber(snapshot.celebrations.upcomingBirthdays)}, anniversaries ${formatNumber(snapshot.celebrations.upcomingAnniversaries)}, memberships ${formatNumber(snapshot.celebrations.upcomingMemberships)}).`,
    dataPackContext
  ].join('\n');
};

const buildStarters = (snapshot) => {
  const starters = [
    'Give me a quick health check of our church metrics.',
    'What attendance trends stand out from the last six months?',
    'Where should we focus our next 30 days of ministry efforts?'
  ];

  if (snapshot) {
    if (snapshot.members.inactive > 0) {
      starters.push(`We have ${formatNumber(snapshot.members.inactive)} inactive members. What re-engagement plan do you suggest?`);
    }
    if (snapshot.donations.growthRate < 0) {
      starters.push(`Giving is down about ${Math.abs(Math.round(snapshot.donations.growthRate))}%. What should we do next?`);
    }
    if (snapshot.attendance.sundayServiceRate > 0 && snapshot.attendance.sundayServiceRate < 50) {
      starters.push(`Sunday service participation is around ${formatNumber(snapshot.attendance.sundayServiceRate)}%. How can we improve it?`);
    }
    if (snapshot.events.needingVolunteers > 0) {
      starters.push(`We have ${formatNumber(snapshot.events.needingVolunteers)} upcoming events needing volunteers. How can we recruit effectively?`);
    }
    if (snapshot.tasks.overdue > 0) {
      starters.push(`We have ${formatNumber(snapshot.tasks.overdue)} overdue tasks. What should be our priorities this week?`);
    }
    if (snapshot.donors.topDonors.length > 0) {
      starters.push('Who are our top givers this year and how can we thank them?');
    }
    if (snapshot.celebrations.totalUpcoming > 0) {
      starters.push('How can we leverage upcoming birthdays and anniversaries to build engagement?');
    }
  }

  return starters.slice(0, 6);
};

export const aiChatService = {
  async getSnapshot({ forceRefresh = false } = {}) {
    if (forceRefresh) {
      dashboardService.clearCache();
    }

    const [organizationName, dashboardData] = await Promise.all([
      getOrganizationName(),
      dashboardService.getDashboardData()
    ]);

    const dataPack = await buildDataPack(dashboardData?.organizationId);

    return buildSnapshot(organizationName, dashboardData, dataPack);
  },

  buildContext,

  buildStarters,

  async sendMessage({ messages, snapshot }) {
    const response = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages,
        context: buildContext(snapshot),
        model: CHAT_MODEL,
        max_tokens: 400
      })
    });

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error || 'AI chat service unavailable');
    }

    const result = await response.json();
    return result.reply || 'No response available.';
  }
};

export default aiChatService;
